import React from "react";
import { soleProprietorFieldConfig, principleFieldConfig } from "../institute.config";
import { useTranslation } from "react-i18next";
import type { OwnershipDetails } from "../types/types";

// Field configuration type
interface FieldConfig {
	name: string;
	label: string;
	icon: React.ReactNode;
	value: string;
}

interface OwnershipDetailsStepProps {
	data?: OwnershipDetails;
}

const OwnershipDetailsStep: React.FC<OwnershipDetailsStepProps> = ({ data }) => {
	const { t } = useTranslation();

	// Map ownership data to field configs
	const soleProprietorConfig = soleProprietorFieldConfig.map(field => ({
		...field,
		value: data?.[field.name as keyof OwnershipDetails]?.toString() || "N/A",
	}));

	const principleConfig = principleFieldConfig.map(field => ({
		...field,
		value: data?.[field.name as keyof OwnershipDetails]?.toString() || "N/A",
	}));

	const renderFieldSection = (title: string, fields: FieldConfig[]) => (
		<div className="space-y-4">
			<div className="text-md font-semibold text-gray-800">{title}</div>
			<div className="grid grid-cols-1 md:grid-cols-4 gap-4">
				{fields?.map(field => (
					<div
						key={field.name}
						className="flex items-center gap-3 p-4 rounded-lg bg-white border border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-sm hover:shadow-md">
						<div className="flex-shrink-0 w-8 h-8 bg-gray-400 rounded-lg flex items-center justify-center shadow-sm">
							<div className="text-white text-base drop-shadow-sm">{field.icon}</div>
						</div>
						<div className="flex-1 min-w-0">
							<div className="text-sm font-semibold text-gray-900 tracking-wide mb-1">
								{field.label}
								<span className="text-red-500 ml-1">*</span>
							</div>
							<div className="text-sm font-medium text-gray-500 truncate">{field.value}</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);

	return (
		<div className="space-y-8 p-2">
			{renderFieldSection(t("components.instituteDetails.ownership.soleProprietor"), soleProprietorConfig)}
			{renderFieldSection(t("components.instituteDetails.ownership.principle"), principleConfig)}
		</div>
	);
};

export default OwnershipDetailsStep;
